import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Robot, Plus, Trash, Pencil } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SupportBot() {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyword, setNewKeyword] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [editingReply, setEditingReply] = useState(null);

  useEffect(() => {
    fetchQuickReplies();
  }, []);

  const fetchQuickReplies = async () => {
    try {
      const response = await axios.get(`${API}/settings/quick-replies`);
      setReplies(response.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddReply = async (e) => {
    e.preventDefault();
    if (!newKeyword.trim() || !newResponse.trim()) {
      toast.error('Both fields are required');
      return;
    }
    try {
      await axios.post(`${API}/settings/quick-replies`, {
        keyword: newKeyword.trim(),
        response_text: newResponse.trim()
      });
      toast.success('Quick reply added!');
      setNewKeyword('');
      setNewResponse('');
      fetchQuickReplies();
    } catch (err) {
      toast.error('Failed to add quick reply');
    }
  };

  const handleDeleteReply = async (id) => {
    if (!window.confirm('Delete this quick reply?')) return;
    try {
      await axios.delete(`${API}/settings/quick-replies/${id}`);
      toast.success('Quick reply deleted');
      fetchQuickReplies();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex items-center gap-3 mb-6 border-b pb-3">
        <Robot size={28} className="text-purple-500" />
        <div>
          <h2 className="text-xl font-bold text-gray-800">Support Bot Quick Replies</h2>
          <p className="text-sm text-gray-500">Configure quick replies and auto-responses for Support Bot</p>
        </div>
      </div>

      <form onSubmit={handleAddReply} className="mb-6 space-y-4 bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold text-gray-700">Add Quick Reply Option</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Keyword / Command (e.g. Hi, Deposit)"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <input
            type="text"
            placeholder="Auto Response Text"
            value={newResponse}
            onChange={(e) => setNewResponse(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <button
          type="submit"
          className="bg-green-500 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-green-600"
        >
          <Plus size={18} weight="bold" /> Add Reply
        </button>
      </form>

      {loading ? (
        <p className="text-gray-500 text-center">Loading quick replies...</p>
      ) : (
        <div className="space-y-3">
          {replies.map((reply) => (
            <div key={reply.id} className="flex items-start justify-between p-3 border rounded-lg hover:bg-gray-50">
              <div>
                <span className="inline-block bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded font-mono mb-1 font-bold">
                  {reply.keyword}
                </span>
                <p className="text-gray-700 text-sm">{reply.response_text}</p>
              </div>
              <button
                onClick={() => handleDeleteReply(reply.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
              >
                <Trash size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
